import React from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';

import { containerStyles } from '../../styles/layout';
import { rem } from '../../utilities/style';
import { body, headers } from '../../styles/type';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';

import VideoPlayer from '../VideoPlayer';
import ButtonText from '../buttons/ButtonText';
import Image from '../Image';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  padding: ${rem(40)} 0;
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Content = styled.div`
  padding: ${rem(32)} ${rem(20)};
  margin: 0 ${rem(-20)};
  background-color: ${colors.blue['50']};

  ${media('>tiny')} {
    padding: ${rem(32)};
    margin: 0;
  }

  ${media('>small')} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${rem(48)};
    padding: ${rem(48)};
  }
`;

const MediaContent = styled.div`
  flex: 0 1 ${rem(584)};
`;

const StyledImage = styled(Image)`
  border-radius: ${rem(10)};
`;

const Video = styled.div`
  border-radius: ${rem(10)};
  overflow: hidden;

  ${media('<=small')} {
    margin-bottom: ${rem(32)};
  }
`;

const TextContent = styled.div`
  flex: 0 1 ${rem(488)};
  margin: auto 0;
`;

const LogoContainer = styled.div`
  margin-bottom: ${rem(20)};
  display: flex;
  align-items: center;
  column-gap: ${rem(24)};
  row-gap: ${rem(16)};
  flex-wrap: wrap;
  justify-content: space-between;
`;

const Logo = styled(Image)`
  max-height: ${rem(45)};
`;

const DesktopButton = styled(ButtonText)`
  ${media('<=small')} {
    display: none;
  }
`;

const MobileButton = styled(ButtonText)`
  margin-top: ${rem(40)};

  ${media('>small')} {
    display: none;
  }
`;

const Text = styled.p`
  ${body.regular}
  color: ${colors.gray['600']};
  margin-bottom: ${rem(20)};
`;

const Attribution = styled.p`
  ${headers.h6}
  margin-bottom: ${rem(32)};

  ${media('>small')} {
    margin-bottom: ${rem(48)};
  }
`;

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${rem(48)};
  row-gap: ${rem(16)};
`;

const Stat = styled.div`

`;

const StatMetric = styled.p`
  ${headers.h2}
  margin-bottom: ${rem(4)};
`;

const StatText = styled.p`
  ${body.regular}
  color: ${colors.gray['600']};
`;

const CaseStudy = ({
  logo,
  linkText,
  linkUrl,
  text,
  attribution,
  stat1Metric,
  stat1Text,
  stat2Metric,
  stat2Text,
  image,
  video
}) => (
  <Container>
    <ContentWrapper>
      <Content>
        <MediaContent>
          {image && !video && (
            <StyledImage image={image} />
          )}
          {video && (
            <Video>
              <VideoPlayer
                url={video.videoUrl}
                title={video.title}
                description={video?.description?.description}
                thumbnailUrl={video.thumbnail?.file?.url}
                thumbnailImage={video.thumbnail?.gatsbyImageData}
                videoBorder={video?.videoBorder}
                uploadDate={video.createdAt}
              />
            </Video>
          )}
        </MediaContent>
        <TextContent>
          <LogoContainer>
            {logo && <Logo image={logo} />}
            {linkText && linkUrl && (
              <DesktopButton arrow to={linkUrl}>
                <TextRenderer text={linkText} />
              </DesktopButton>
            )}
          </LogoContainer>
          {text && (
            <Text>
              <TextRenderer text={text} richText />
            </Text>
          )}
          {attribution && (
            <Attribution>
              <TextRenderer text={attribution} />
            </Attribution>
          )}
          <Stats>
            {stat1Metric && stat1Text && (
              <Stat>
                <StatMetric>
                  <TextRenderer text={stat1Metric} />
                </StatMetric>
                <StatText>
                  <TextRenderer text={stat1Text} />
                </StatText>
              </Stat>
            )}
            {stat2Metric && stat2Text && (
              <Stat>
                <StatMetric>
                  <TextRenderer text={stat2Metric} />
                </StatMetric>
                <StatText>
                  <TextRenderer text={stat2Text} />
                </StatText>
              </Stat>
            )}
          </Stats>
          {linkText && linkUrl && (
            <MobileButton arrow to={linkUrl}>
              <TextRenderer text={linkText} />
            </MobileButton>
          )}
        </TextContent>
      </Content>
    </ContentWrapper>
  </Container>
);

CaseStudy.propTypes = {
  logo: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object,
  }),
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
  text: PropTypes.string,
  attribution: PropTypes.string,
  stat1Metric: PropTypes.string,
  stat1Text: PropTypes.string,
  stat2Metric: PropTypes.string,
  stat2Text: PropTypes.string,
  image: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object,
  }),
  video: PropTypes.shape({
    videoUrl: PropTypes.string,
    title: PropTypes.string,
    createdAt: PropTypes.string,
    description: PropTypes.shape({
      description: PropTypes.string
    }),
    annotationImage: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object
    }),
    text: PropTypes.shape({
      raw: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      references: PropTypes.arrayOf(PropTypes.object)
    }),
    thumbnail: PropTypes.shape({
      url: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object,
      file: PropTypes.shape({
        url: PropTypes.string
      })
    })
  }),
};

export const query = graphql`
  fragment CaseStudyFragment on ContentfulCaseStudy {
    id
    __typename
    logo {
      gatsbyImageData(height: 45, quality: 75)
      ...ImageAssetFragment
    }
    linkText
    linkUrl
    text {
      raw
    }
    attribution
    stat1Metric
    stat1Text
    stat2Metric
    stat2Text
    image {
      gatsbyImageData(width: 584 quality: 75, resizingBehavior: FILL)
    }
    video {
      videoUrl
      title
      createdAt
      description {
        description
      }
      thumbnail {
        url
        gatsbyImageData(width: 584, height: 329, quality: 75, resizingBehavior: FILL)
        file {
          url
        }
      }
    }
  }
`;

export default CaseStudy;
